import { useEffect, useState } from "react";
import { api, authHeader } from "@/lib/api";
import { toast } from "sonner";
import QrCodeSection from "@/components/QrCodeSection";
import AdminGallery from "@/components/AdminGallery";
import {
  Lock,
  LogOut,
  Upload,
  FileText,
  Image as ImageIcon,
  Save,
  KeyRound,
  Loader2,
  Clock,
} from "lucide-react";

const inputCls =
  "w-full bg-[#0b1120] border border-[#d4af37]/30 rounded-sm px-3 py-2.5 text-sm text-[#fdfbf7] focus:outline-none focus:border-[#d4af37] transition-colors";
const labelCls =
  "block text-[11px] uppercase tracking-[0.15em] text-[#d4af37]/80 mb-1.5 font-medium";

const Field = ({ k, label, textarea, value, onChange }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {textarea ? (
      <textarea
        rows={4}
        value={value || ""}
        onChange={(e) => onChange(k, e.target.value)}
        className={inputCls}
        data-testid={`field-${k}`}
      />
    ) : (
      <input
        value={value || ""}
        onChange={(e) => onChange(k, e.target.value)}
        className={inputCls}
        data-testid={`field-${k}`}
      />
    )}
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-[#0f172a] border border-[#d4af37]/20 rounded-md p-5">
    <h3 className="font-serif text-2xl text-[#d4af37] flex items-center gap-2 mb-4">
      <Icon className="w-5 h-5" /> {title}
    </h3>
    {children}
  </div>
);

const LoginView = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", {
        password: password.trim(),
      });
      localStorage.setItem("gd_admin_token", data.token);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore di accesso");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-[#0f172a] border border-[#d4af37]/25 rounded-md p-8 gd-fade-up"
      >
        <div className="text-center mb-6">
          <Lock className="w-8 h-8 text-[#d4af37] mx-auto mb-3" />
          <h1 className="font-serif text-3xl text-[#d4af37]">Area Admin</h1>
          <p className="text-xs text-[#e0ddcf]/60 mt-1">
            Giardino del Gusto
          </p>
        </div>
        <label className={labelCls} htmlFor="pwd">
          Password
        </label>
        <input
          id="pwd"
          type="password"
          data-testid="admin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={inputCls}
          autoFocus
        />
        <button
          type="submit"
          data-testid="admin-login-button"
          disabled={loading}
          className="mt-5 w-full bg-[#d4af37] text-[#060a14] font-semibold px-6 py-3 rounded-sm active:scale-95 transition-transform flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            "Accedi"
          )}
        </button>
      </form>
    </div>
  );
};

const UploadRow = ({ label, testId, accept, current, onUpload, uploading }) => {
  const [file, setFile] = useState(null);
  return (
    <div>
      {current}
      <input
        type="file"
        accept={accept}
        data-testid={`${testId}-input`}
        onChange={(e) => setFile(e.target.files[0])}
        className="block w-full text-xs text-[#e0ddcf] file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-[#d4af37]/15 file:text-[#d4af37] file:text-xs file:font-medium mt-2"
      />
      <button
        onClick={() => file && onUpload(file)}
        disabled={!file || uploading}
        data-testid={`${testId}-button`}
        className="mt-3 w-full bg-transparent border border-[#d4af37] text-[#d4af37] px-4 py-2.5 rounded-sm hover:bg-[#d4af37]/10 active:bg-[#d4af37]/20 transition-colors flex items-center justify-center gap-2 disabled:opacity-40 text-sm font-medium"
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <Upload className="w-4 h-4" /> {label}
          </>
        )}
      </button>
    </div>
  );
};

const Dashboard = ({ onLogout }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [notesText, setNotesText] = useState("");
  const [newPwd, setNewPwd] = useState("");

  const load = () => {
    api
      .get("/admin/settings", { headers: authHeader() })
      .then((r) => {
        setSettings(r.data);
        setNotesText((r.data.notes || []).join("\n"));
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem("gd_admin_token");
          onLogout();
        }
      });
  };

  useEffect(load, []); // eslint-disable-line

  const field = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const setHour = (i, key, val) => {
    setSettings((s) => {
      const hours = s.hours.map((h, idx) =>
        idx === i ? { ...h, [key]: val } : h
      );
      return { ...s, hours };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: settings.name,
        subtitle: settings.subtitle,
        payoff: settings.payoff,
        address: settings.address,
        address_note: settings.address_note,
        maps_query: settings.maps_query,
        phone: settings.phone,
        whatsapp: settings.whatsapp,
        instagram: settings.instagram,
        google_review_url: settings.google_review_url,
        tripadvisor_url: settings.tripadvisor_url,
        description: settings.description,
        takeout_text: settings.takeout_text,
        hours: settings.hours,
        notes: notesText.split("\n").map((l) => l.trim()).filter(Boolean),
        allergen_note: settings.allergen_note,
      };
      const { data } = await api.put("/admin/settings", payload, {
        headers: authHeader(),
      });
      setSettings(data);
      toast.success("Modifiche salvate");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const uploadPdf = async (file) => {
    setUploadingPdf(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/upload-pdf", fd, { headers: authHeader() });
      toast.success("Menu aggiornato! Il QR code funziona come sempre.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento PDF");
    } finally {
      setUploadingPdf(false);
    }
  };

  const uploadLogo = async (file) => {
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/upload-logo", fd, { headers: authHeader() });
      toast.success("Logo aggiornato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const changePassword = async () => {
    if (newPwd.length < 4) {
      toast.error("Minimo 4 caratteri");
      return;
    }
    try {
      await api.post(
        "/admin/change-password",
        { new_password: newPwd },
        { headers: authHeader() }
      );
      setNewPwd("");
      toast.success("Password aggiornata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  if (!settings) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#d4af37] animate-spin" />
      </div>
    );
  }

  const F = (props) =>
    Field({ ...props, value: settings[props.k], onChange: field });

  return (
    <div className="max-w-xl mx-auto px-5 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-[#d4af37]">Pannello</h1>
        <button
          onClick={() => {
            localStorage.removeItem("gd_admin_token");
            onLogout();
          }}
          data-testid="logout-button"
          className="flex items-center gap-1.5 text-xs text-[#e0ddcf]/70 border border-[#d4af37]/30 rounded-sm px-3 py-2 active:text-[#d4af37]"
        >
          <LogOut className="w-4 h-4" /> Esci
        </button>
      </div>

      <Section title="Menu PDF" icon={FileText}>
        <p className="text-xs text-[#e0ddcf]/70 mb-1">
          Il nuovo PDF sostituisce quello attuale mantenendo lo stesso URL: il
          QR code sui tavoli continua a funzionare.
        </p>
        {settings.menu_updated_at && (
          <p className="text-[11px] text-[#d4af37]/80 mb-2">
            Ultimo aggiornamento:{" "}
            {new Date(settings.menu_updated_at).toLocaleString("it-IT")}
          </p>
        )}
        <UploadRow
          label="Carica nuovo menu"
          testId="upload-pdf"
          accept="application/pdf"
          uploading={uploadingPdf}
          onUpload={uploadPdf}
          current={
            <p className="text-xs text-[#e0ddcf]/60">
              {settings.has_pdf
                ? `Menu attuale caricato (versione ${settings.pdf_version}).`
                : "Nessun menu caricato."}
            </p>
          }
        />
      </Section>

      <QrCodeSection
        restaurantName={settings.name}
        subtitle={settings.subtitle}
      />

      <Section title="Logo" icon={ImageIcon}>
        <UploadRow
          label="Carica logo"
          testId="upload-logo"
          accept="image/*"
          uploading={uploadingLogo}
          onUpload={uploadLogo}
          current={
            <p className="text-xs text-[#e0ddcf]/60">
              {settings.has_logo ? "Logo caricato." : "Nessun logo caricato."}
            </p>
          }
        />
      </Section>

      <AdminGallery />

      <Section title="Informazioni" icon={Save}>
        <div className="space-y-4">
          {F({ k: "name", label: "Nome" })}
          {F({ k: "subtitle", label: "Sottotitolo" })}
          {F({ k: "payoff", label: "Payoff" })}
          {F({ k: "phone", label: "Telefono" })}
          {F({ k: "whatsapp", label: "WhatsApp" })}
          {F({ k: "instagram", label: "Instagram (handle)" })}
          {F({ k: "google_review_url", label: "Link recensioni Google" })}
          {F({ k: "tripadvisor_url", label: "Link recensioni Tripadvisor" })}
          {F({ k: "address", label: "Indirizzo" })}
          {F({ k: "address_note", label: "Nota indirizzo" })}
          {F({ k: "maps_query", label: "Ricerca Google Maps" })}
          {F({ k: "takeout_text", label: "Testo banner asporto" })}
          {F({ k: "description", label: "Descrizione", textarea: true })}
          {F({ k: "allergen_note", label: "Nota allergeni", textarea: true })}
          <div>
            <label className={labelCls}>Da sapere (una voce per riga)</label>
            <textarea
              rows={5}
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              className={inputCls}
              data-testid="field-notes"
            />
          </div>
        </div>
      </Section>

      <Section title="Orari" icon={Clock}>
        <div className="space-y-2">
          {settings.hours.map((h, i) => (
            <div
              key={i}
              className="flex items-center gap-2 text-sm"
              data-testid={`hours-row-${i}`}
            >
              <span className="w-20 shrink-0 text-[#e0ddcf]">{h.day}</span>
              <input
                type="time"
                value={h.open}
                disabled={h.closed}
                onChange={(e) => setHour(i, "open", e.target.value)}
                className={`${inputCls} py-1.5 disabled:opacity-40`}
              />
              <span className="text-[#d4af37]">–</span>
              <input
                type="time"
                value={h.close}
                disabled={h.closed}
                onChange={(e) => setHour(i, "close", e.target.value)}
                className={`${inputCls} py-1.5 disabled:opacity-40`}
              />
              <label className="flex items-center gap-1 text-[11px] text-[#e0ddcf]/70 shrink-0">
                <input
                  type="checkbox"
                  checked={h.closed}
                  onChange={(e) => setHour(i, "closed", e.target.checked)}
                  className="accent-[#d4af37]"
                />
                Chiuso
              </label>
            </div>
          ))}
        </div>
      </Section>

      <button
        onClick={save}
        disabled={saving}
        data-testid="save-settings-button"
        className="w-full bg-[#d4af37] text-[#060a14] font-semibold px-6 py-3.5 rounded-sm active:scale-95 transition-transform flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {saving ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <Save className="w-4 h-4" /> Salva modifiche
          </>
        )}
      </button>

      <Section title="Cambia password" icon={KeyRound}>
        <input
          type="password"
          value={newPwd}
          onChange={(e) => setNewPwd(e.target.value)}
          placeholder="Nuova password"
          className={inputCls}
          data-testid="new-password-input"
        />
        <button
          onClick={changePassword}
          data-testid="change-password-button"
          className="mt-3 w-full bg-transparent border border-[#d4af37] text-[#d4af37] px-4 py-2.5 rounded-sm hover:bg-[#d4af37]/10 active:bg-[#d4af37]/20 transition-colors text-sm font-medium"
        >
          Aggiorna password
        </button>
      </Section>
    </div>
  );
};

const AdminPage = () => {
  const [authed, setAuthed] = useState(
    !!localStorage.getItem("gd_admin_token")
  );
  return authed ? (
    <Dashboard onLogout={() => setAuthed(false)} />
  ) : (
    <LoginView onSuccess={() => setAuthed(true)} />
  );
};

export default AdminPage;
